import React, { useRef, useEffect } from 'react';

const StarField = ({ particleCount = 500, speed = 0.5, starSize = 1, twinkle = true }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const glRef = useRef(null);
  const programRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }
    glRef.current = gl;

    // Vertex shader
    const vertexShaderSource = `
      attribute vec2 a_position;
      attribute float a_size;
      attribute float a_opacity;
      uniform vec2 u_resolution;
      varying float v_opacity;
      
      void main() {
        vec2 position = (a_position / u_resolution) * 2.0 - 1.0;
        position.y = -position.y;
        gl_Position = vec4(position, 0.0, 1.0);
        gl_PointSize = a_size;
        v_opacity = a_opacity;
      }
    `;

    // Fragment shader
    const fragmentShaderSource = `
      precision mediump float;
      varying float v_opacity;
      
      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);
        if (dist > 0.5) discard;
        
        float alpha = (1.0 - dist * 2.0) * v_opacity;
        gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
      }
    `;

    // Compile shader
    const createShader = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compile error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    // Create program
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return;
    }
    
    programRef.current = program;
    gl.useProgram(program);

    // Get attribute and uniform locations
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const sizeLocation = gl.getAttribLocation(program, 'a_size');
    const opacityLocation = gl.getAttribLocation(program, 'a_opacity');
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');

    // Create buffers
    const positionBuffer = gl.createBuffer();
    const sizeBuffer = gl.createBuffer();
    const opacityBuffer = gl.createBuffer();

    // Initialize particles array
    const particles = [];
    
    // Resize canvas and viewport
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      
      // Don't redistribute particles - just let them maintain their positions
      // This avoids expensive recalculation on every resize
    };

    // Initialize canvas size first
    resizeCanvas();

    // Create particles after canvas is sized
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: (Math.random() * 4 + 1) * starSize,
        opacity: Math.random(),
        twinkleSpeed: Math.random() * 0.02 + 0.01,
        phase: Math.random() * Math.PI * 2
      });
    }
    particlesRef.current = particles;
    
    // Debounced resize handler to improve performance
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeCanvas, 150);
    };
    
    window.addEventListener('resize', debouncedResize);

    // Animation loop
    const animate = (time) => {
      // Update particles
      const positions = new Float32Array(particleCount * 2);
      const sizes = new Float32Array(particleCount);
      const opacities = new Float32Array(particleCount);

      particles.forEach((particle, i) => {
        let opacity = particle.opacity;
        
        if (twinkle) {
          // Update twinkle
          particle.phase += particle.twinkleSpeed * speed;
          const twinkleEffect = (Math.sin(particle.phase) + 1) * 0.5;
          opacity *= twinkleEffect;
        }
        
        positions[i * 2] = particle.x;
        positions[i * 2 + 1] = particle.y;
        sizes[i] = particle.size;
        opacities[i] = opacity;
      });

      // Clear canvas
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      
      // Enable blending for transparency
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

      // Update buffers
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, sizes, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(sizeLocation);
      gl.vertexAttribPointer(sizeLocation, 1, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, opacityBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, opacities, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(opacityLocation);
      gl.vertexAttribPointer(opacityLocation, 1, gl.FLOAT, false, 0, 0);

      // Draw particles
      gl.drawArrays(gl.POINTS, 0, particleCount);

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (gl) {
        gl.deleteProgram(program);
        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);
        gl.deleteBuffer(positionBuffer);
        gl.deleteBuffer(sizeBuffer);
        gl.deleteBuffer(opacityBuffer);
      }
    };
  }, [particleCount, speed, starSize, twinkle]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}
    />
  );
};

export default StarField;